import React from 'react';
import { Icon } from 'antd';
import styled from 'styled-components';

const Layout = styled.div`
  position: relative;
  overflow: hidden;
  video {
    width: 100%;
    height: 100%;
    transform: scale3d(1, 1, 1);
  }
`;

const Mask = styled.div<{ isPlaying: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  background: #000000;
  opacity: 0.53;
  position: absolute;
  top: 0;
  left: 0;
  display: ${(props) => (props.isPlaying ? 'none' : 'flex')};
  ${Layout}:hover & {
    display: flex;
  }
`;

interface VideoPlayerProps {
  width: number;
  height: number;
  preload?: string;
  videoSrc: string;
  fileType?: string;
  style?: React.CSSProperties;
}

interface VideoPlayerState {
  isPlaying: boolean;
}

export class VideoPlayer extends React.Component<VideoPlayerProps, VideoPlayerState> {
  public static videos: any = {};
  public static defaultProps = {
    preload: 'meta',
  };

  public video = React.createRef<HTMLVideoElement>();
  public bgImg = React.createRef<HTMLImageElement>();
  private id: string;

  constructor(props: VideoPlayerProps) {
    super(props);
    this.state = {
      isPlaying: false,
    };
    this.id = Math.random().toString(36).substring(4);
    VideoPlayer.videos[this.id] = this;
  }

  public componentDidMount() {
    this.video.current.addEventListener('loadeddata', this.onloaddata);
    this.video.current.addEventListener('timeupdate', this.onloaddata);
    this.video.current.addEventListener('pause', this.onpause);
    this.video.current.addEventListener('ended', this.onended);
  }

  public componentWillUnmount() {
    if (this.state.isPlaying) {
      this.video.current.pause();
    }
    this.video.current.removeEventListener('loadeddata', this.onloaddata);
    this.video.current.removeEventListener('timeupdate', this.onloaddata);
    this.video.current.addEventListener('pause', this.onpause);
    this.video.current.removeEventListener('ended', this.onended);
    delete VideoPlayer.videos[this.id];
  }

  public togglePlay = (e: any) => {
    e.stopPropagation();
    const video = this.video;
    if (video.current) {
      if (this.state.isPlaying) {
        video.current.pause();
      } else {
        const ids = Object.keys(VideoPlayer.videos);
        for (const id of ids) {
          const otherVideo = VideoPlayer.videos[id];
          if (otherVideo && otherVideo.id !== this.id) {
            otherVideo.setState({
              isPlaying: false,
            });
            if (otherVideo.video.current) {
              otherVideo.video.current.pause();
            }
          }
        }
        video.current.play();
      }
      this.setState({
        isPlaying: !this.state.isPlaying,
      });
    }
  };

  public onloaddata = () => {
    const canvas = document.createElement('canvas');
    canvas.width = this.props.width;
    canvas.height = this.props.height;
    const context = canvas.getContext('2d');
    context.drawImage(this.video.current, 0, 0, this.props.width, this.props.height);
    const dataURL = canvas.toDataURL();
    if (this.bgImg.current) {
      this.bgImg.current.setAttribute('src', dataURL);
    }
  };

  public onpause = () => {
    this.video.current && this.video.current.pause();
    this.setState({ isPlaying: false });
  };

  public onended = () => {
    this.video.current.currentTime = 0;
    this.setState({
      isPlaying: false,
    });
  };

  public render() {
    const { isPlaying } = this.state;
    const { width, height, videoSrc, preload } = this.props;
    return (
      <Layout data-hoverable-media={true}>
        <img
          ref={this.bgImg}
          style={{
            filter: 'blur(7px)',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100%',
            height: '100%',
          }}
        />
        <video
          width={width}
          height={height}
          crossOrigin={'anonymous'}
          ref={this.video}
          preload={preload}
          src={videoSrc}
        />
        <Mask isPlaying={isPlaying}>
          {videoSrc && (
            <Icon
              style={{
                color: '#fff',
                fontSize: '36px',
                cursor: 'pointer',
              }}
              type={isPlaying ? 'pause-circle' : 'play-circle'}
              theme={'filled'}
              onClick={this.togglePlay}
            />
          )}
        </Mask>
      </Layout>
    );
  }
}
